import { t } from 'i18next';
import * as yup from 'yup';

import { REMINDER_CONSTRAINTS } from '@/components/atoms/ReminderTimeSelect/ReminderTimeSelect';
import { SWITCH_CHANNEL_DESTINATIONS } from '@/shared/types/action';

import {
  LENGTH_S,
  LENGTH_M,
  LENGTH_XL,
  LENGTH_XXL,
  LENGTH_1024,
  MAX_DIALOG_NAME_LENGTH,
  MAX_SIP_HEADERS,
  HANDOVER_OPTIONS,
  VOICE_HANDOVER_TRANSPORT,
} from '../constants';
import { e164PhonePattern, sipUriPattern } from '../patterns';
import { urlSchema } from '../schema-builders';

export const switchChannelActionSchema = yup.object().shape({
  channel: yup
    .string()
    .required(() => t('validation.required'))
    .oneOf([...SWITCH_CHANNEL_DESTINATIONS], () => t('dialog.switch_channel.invalid_channel')),
});

export const handOverActionSchema = yup.object().shape({
  handover: yup.string(),
  department_id: yup.string().when('handover', (handover, schema) => {
    if (handover[0] === HANDOVER_OPTIONS.ZENDESK_DEPARTMENT_ROUTING) {
      return schema
        .max(LENGTH_M, () => t('validation.less_than', { 0: LENGTH_M }))
        .required(() => t('validation.required'));
    }
    return schema.strip();
  }),
  voice_handover_destination: yup.string().when('handover', (handover, schema) => {
    if (handover[0] === HANDOVER_OPTIONS.VOICE_HANDOVER) {
      return schema
        .max(LENGTH_M)
        .required(() => t('validation.required'))
        .test(
          'sip-or-e164',
          () => t('actions.handover.voice_handover.invalid_destination'),
          (value) => {
            if (!value) {
              return false;
            }
            // Check if it's a valid E.164 phone number or SIP URI
            return e164PhonePattern.test(value) || sipUriPattern.test(value);
          }
        );
    }
    return schema.strip();
  }),
  voice_handover_attributes: yup.mixed().when('handover', (handover, schema) => {
    if (handover[0] === HANDOVER_OPTIONS.VOICE_HANDOVER) {
      return yup
        .object()
        .test(
          'valid-sip-headers',
          () => t('actions.handover.voice_handover.invalid_headers'),
          (obj) => {
            if (!obj) {
              return true;
            }
            const entries = Object.entries(obj);
            if (entries.length > MAX_SIP_HEADERS) {
              return false;
            }
            return entries.every(
              ([key, val]) =>
                typeof key === 'string' &&
                key.length <= LENGTH_M &&
                typeof val === 'string' &&
                val.length <= LENGTH_XL
            );
          }
        )
        .optional()
        .default(undefined);
    }
    return schema.strip();
  }),
  voice_handover_transport: yup
    .string()
    .when(['handover', 'voice_handover_destination'], ([handover, destination], schema) => {
      if (handover !== HANDOVER_OPTIONS.VOICE_HANDOVER) {
        return schema.strip();
      }
      // refer is only valid for a SIP destination; a phone or empty destination allows dial only.
      const allowed =
        destination && sipUriPattern.test(destination)
          ? [VOICE_HANDOVER_TRANSPORT.DIAL, VOICE_HANDOVER_TRANSPORT.REFER]
          : [VOICE_HANDOVER_TRANSPORT.DIAL];
      return schema.oneOf(allowed, () =>
        t('actions.handover.voice_handover.transport_requires_sip')
      );
    }),
});

export const imageActionSchema = yup.object().shape({
  url: yup
    .string()
    .url(() => t('validation.invalid_url'))
    .max(LENGTH_XXL, () => t('validation.less_than', { 0: LENGTH_XXL }))
    .required(() => t('validation.required')),
  name: yup
    .string()
    .max(LENGTH_S, () => t('validation.less_than', { 0: LENGTH_S }))
    .required(() => t('validation.required')),
});

export const textActionSchema = yup.object({
  actionTexts: yup.array().of(
    yup.object({
      textValue: yup
        .string()
        .trim()
        .required(() => t('validation.required'))
        .max(LENGTH_XXL, () => t('validation.less_than', { 0: LENGTH_XXL })),
    })
  ),
});

export const fileActionSchemaNew = imageActionSchema;

const triggerNodeSchema = yup.lazy((value) =>
  typeof value === 'string'
    ? yup.string().required(() => t('validation.required'))
    : yup
        .object({
          dialog_name: yup.string(),
          value: yup.string(),
          label: yup.string(),
          node_type: yup.string(),
        })
        .required(() => t('validation.required'))
);

export const eventActionSchema2 = yup.object({ jump_to: triggerNodeSchema });

export const replayActionSchema = yup.object().shape({
  replay: yup.string().required(() => t('validation.required')),
  customMessage: yup.string().when('replay', (replay, schema) => {
    if (replay[0] === 'default') {
      return schema.strip();
    }
    return schema
      .min(1, () => t('validation.at_least', { 0: 1 }))
      .max(LENGTH_1024, () => t('validation.less_than', { 0: LENGTH_1024 }))
      .required(() => t('validation.required'));
  }),
});

export const surveyActionSchema = yup.object({
  survey_type: yup.string().required(() => t('validation.required')),
  url: urlSchema,
  name: yup
    .string()
    .required(() => t('validation.required'))
    .max(MAX_DIALOG_NAME_LENGTH, () => t('validation.less_than', { 0: MAX_DIALOG_NAME_LENGTH })),
  label: yup
    .string()
    .required(() => t('validation.required'))
    .min(1, () => t('validation.at_least', { 0: 1 }))
    .max(LENGTH_S, () => t('validation.less_than', { 0: LENGTH_S })),
  height: yup.string().max(LENGTH_S, () => t('validation.less_than', { 0: LENGTH_S })),
});

export const webviewActionSchema = yup.object().shape({
  url: urlSchema,
  name: yup
    .string()
    .required(() => t('validation.required'))
    .max(MAX_DIALOG_NAME_LENGTH, () => t('validation.less_than', { 0: MAX_DIALOG_NAME_LENGTH })),
  label: yup
    .string()
    .required(() => t('validation.required'))
    .min(1, () => t('validation.at_least', { 0: 1 }))
    .max(LENGTH_S, () => t('validation.less_than', { 0: LENGTH_S })),
  trigger_node_id: yup
    .string()
    .required(() => t('validation.required_field', { 0: 'Trigger_node_id' })),
  height: yup.string().max(LENGTH_S, () => t('validation.less_than', { 0: LENGTH_S })),
  auto_open: yup.boolean(),
});

export const reminderFormSchema = yup.object().shape({
  reminders: yup
    .array()
    .max(REMINDER_CONSTRAINTS.MAX_REMINDERS)
    .test(
      'both-null',
      () => t('validation.required'),
      // A row needs both a duration and a node. A row with neither is left out on save.
      (reminders) => reminders.every((reminder) => !reminder.reminder_seconds === !reminder.node)
    ),
});

export const reminderActionSchema = yup.object().shape({
  reminder_seconds: yup
    .number()
    .min(REMINDER_CONSTRAINTS.MIN_SECONDS)
    .max(REMINDER_CONSTRAINTS.MAX_SECONDS)
    .integer()
    .required(() => t('validation.required')),
  node: yup.string().required(() => t('validation.required')),
});
